/**
 * BuilderStudioPage — "Lovable for Logistics" no-code builder
 *
 * NL description → AI generates DSL artifacts → Preview → Publish
 * Supports: view-template, reaction-rule, virtual-endpoint, bundle, semantic-model, workspace-template, report-model, billing-model, charge-event, report-fact, printable-view-model
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import {
  recordBuilderGovernanceEvent,
  type BuilderGovernanceEventType,
} from '@/lib/builder-governance';
import { useToast } from '@/lib/toast';
import { useChatContextStore } from '@/stores/chat-context-store';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  Wand2, Send, Eye, Upload, Loader2, CheckCircle2, XCircle,
  LayoutList, Zap, Globe, Package, History, ChevronRight, Copy, ExternalLink, GitBranch,
  Monitor, Smartphone, Filter, ArrowUpDown, Search, BarChart3, Calculator,
} from 'lucide-react';
import { BUSINESS_PACKAGE_SHOWCASES, getBusinessPackageShowcase } from './business-package-showcases';

// ── Types ──

type ArtifactType =
  | 'view-template'
  | 'reaction-rule'
  | 'virtual-endpoint'
  | 'bundle'
  | 'semantic-model'
  | 'workspace-template'
  | 'report-model'
  | 'billing-model'
  | 'charge-event'
  | 'report-fact'
  | 'printable-view-model';

interface GenerationResult {
  artifactType: ArtifactType;
  artifact: Record<string, unknown>;
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

interface HistoryItem {
  id: string;
  artifact_type: string;
  description: string;
  valid: boolean;
  published: boolean;
  created_at: string;
  published_slug?: string | null;
  package_install_id?: string | null;
  package_release_id?: string | null;
  package_id?: string | null;
  package_version?: string | null;
  package_install_status?: string | null;
  package_runtime_status?: string | null;
}

interface PublishResult {
  id: string;
  artifactType: ArtifactType;
  status: string;
  draftId?: string;
  releaseId?: string;
  installId?: string;
  runtimeBindings?: Array<{ bindingType: string; bindingRef: string }>;
}

interface BuilderDraftDetail {
  draft: {
    id: string;
    name: string;
    slug: string;
    interviewState?: Record<string, unknown>;
  };
  latestPreflight?: {
    decision?: string;
    result?: {
      recommendedNextAction?: string;
      issues?: Array<{ label?: string; severity?: string; category?: string }>;
    };
  } | null;
  latestSimulation?: {
    mode?: string;
    result?: {
      summary?: string;
      signals?: Array<{ label?: string; severity?: string; category?: string }>;
      mockScenarioRefs?: string[];
      approvalCheckpoints?: string[];
      riskFindings?: Array<{ label?: string; severity?: string; category?: string }>;
      operatorNotes?: string[];
    };
  } | null;
}

type BuilderPrecheckSeverity = 'blocking' | 'review';
type BuilderPrecheckCategory = 'interview' | 'data' | 'policy' | 'sharing' | 'runtime';

interface BuilderPrecheckIssue {
  code: string;
  severity: BuilderPrecheckSeverity;
  category: BuilderPrecheckCategory;
  label: string;
  fieldKey?: string;
}

interface BuilderLocalReadiness {
  decision: 'blocked' | 'review_required' | 'ready';
  issues: BuilderPrecheckIssue[];
  recommendedNextAction: string;
}

const ARTIFACT_OPTIONS: { value: ArtifactType; label: string; labelZh: string; icon: React.ReactNode; desc: string; descZh: string }[] = [
  { value: 'view-template', label: 'View Template', labelZh: '视图模板', icon: <LayoutList className="h-4 w-4" />, desc: 'List, detail, form views', descZh: '列表、详情、表单视图' },
  { value: 'workspace-template', label: 'Workspace Template', labelZh: '工作台模板', icon: <Monitor className="h-4 w-4" />, desc: 'Main + assist + dock + mobile flow', descZh: '主区 + 辅助区 + 右 dock + 移动动作' },
  { value: 'semantic-model', label: 'Semantic Model', labelZh: '语义模型', icon: <GitBranch className="h-4 w-4" />, desc: 'Tenant vocabulary to canonical roles', descZh: '把租户术语映射到 canonical roles' },
  { value: 'report-model', label: 'Report Model', labelZh: '报表模型', icon: <BarChart3 className="h-4 w-4" />, desc: 'Dimensions + measures + drill path', descZh: '维度 + 指标 + drill path' },
  { value: 'billing-model', label: 'Billing Model', labelZh: '计费模型', icon: <Calculator className="h-4 w-4" />, desc: 'Charge family + rate source + posting', descZh: '收费族 + 费率来源 + 过账映射' },
  { value: 'charge-event', label: 'Charge Event', labelZh: '收费事件', icon: <Zap className="h-4 w-4" />, desc: 'Canonical billable event capture rules', descZh: '规范化可计费事件捕获规则' },
  { value: 'report-fact', label: 'Report Fact', labelZh: '报表事实模型', icon: <History className="h-4 w-4" />, desc: 'Canonical facts for analytics and billing truth', descZh: '分析与计费真相层的规范事实模型' },
  { value: 'printable-view-model', label: 'Printable View Model', labelZh: '打印视图模型', icon: <Copy className="h-4 w-4" />, desc: 'Printable document projection and actions', descZh: '打印文档投影与动作定义' },
  { value: 'reaction-rule', label: 'Automation Rule', labelZh: '自动化规则', icon: <Zap className="h-4 w-4" />, desc: 'Event triggers & actions', descZh: '事件触发与自动操作' },
  { value: 'virtual-endpoint', label: 'API Endpoint', labelZh: 'API 端点', icon: <Globe className="h-4 w-4" />, desc: 'Custom query/skill endpoint', descZh: '自定义查询 / 技能端点' },
  { value: 'bundle', label: 'Mini App', labelZh: '迷你应用', icon: <Package className="h-4 w-4" />, desc: 'Full app with views + APIs + rules', descZh: '包含视图、API、规则的完整应用' },
];

function isArtifactType(value: string | null): value is ArtifactType {
  return ARTIFACT_OPTIONS.some((option) => option.value === value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function asRecordArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter((item): item is Record<string, unknown> => Boolean(asRecord(item)))
    : [];
}

function hasNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function buildLocalReadiness(result: GenerationResult | null, publishName: string, isZh: boolean): BuilderLocalReadiness | null {
  if (!result) return null;

  const issues: BuilderPrecheckIssue[] = [];
  const artifact = result.artifact;

  if (!publishName.trim()) {
    issues.push({
      code: 'missing_publish_name',
      severity: 'blocking',
      category: 'data',
      label: isZh ? '发布名称未填写' : 'Publish name is missing',
      fieldKey: 'publishName',
    });
  }

  if (!result.valid) {
    issues.push({
      code: 'invalid_generated_artifact',
      severity: 'blocking',
      category: 'runtime',
      label: isZh ? '生成产物验证未通过' : 'Generated artifact did not pass validation',
      fieldKey: 'artifact',
    });
  }

  switch (result.artifactType) {
    case 'view-template': {
      if (!hasNonEmptyString(artifact.title)) {
        issues.push({
          code: 'missing_view_title',
          severity: 'review',
          category: 'data',
          label: isZh ? '视图标题未明确' : 'View title is not explicit',
          fieldKey: 'artifact.title',
        });
      }
      if (!hasNonEmptyString(artifact.entitySlug)) {
        issues.push({
          code: 'missing_view_entity',
          severity: 'review',
          category: 'data',
          label: isZh ? '视图未绑定清晰实体' : 'View is not bound to a clear entity',
          fieldKey: 'artifact.entitySlug',
        });
      }
      break;
    }
    case 'reaction-rule': {
      const actions = asRecordArray(artifact.actions);
      if (!hasNonEmptyString(artifact.eventType)) {
        issues.push({
          code: 'missing_rule_event',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? '自动化规则缺少事件触发器' : 'Automation rule is missing an event trigger',
          fieldKey: 'artifact.eventType',
        });
      }
      if (actions.length === 0) {
        issues.push({
          code: 'missing_rule_actions',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? '自动化规则没有动作' : 'Automation rule has no actions',
          fieldKey: 'artifact.actions',
        });
      }
      break;
    }
    case 'virtual-endpoint': {
      if (!hasNonEmptyString(artifact.slug)) {
        issues.push({
          code: 'missing_endpoint_slug',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'API endpoint 缺少 slug' : 'API endpoint is missing a slug',
          fieldKey: 'artifact.slug',
        });
      }
      if (!hasNonEmptyString(artifact.execution_type)) {
        issues.push({
          code: 'missing_endpoint_execution_type',
          severity: 'review',
          category: 'runtime',
          label: isZh ? 'API endpoint 执行类型未明确' : 'API endpoint execution type is not explicit',
          fieldKey: 'artifact.execution_type',
        });
      }
      break;
    }
    case 'semantic-model': {
      const entityAliases = asRecordArray(artifact.entityAliases);
      const termSet = asRecord(artifact.termSet);
      if (entityAliases.length === 0) {
        issues.push({
          code: 'missing_semantic_entities',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? '语义模型缺少 entity aliases' : 'Semantic model is missing entity aliases',
          fieldKey: 'artifact.entityAliases',
        });
      }
      if (!entityAliases.some((entry) => entry.canonicalRole === 'job')) {
        issues.push({
          code: 'missing_semantic_job_root',
          severity: 'review',
          category: 'runtime',
          label: isZh ? '建议明确 job root 的语义映射' : 'Recommended: define the job root semantic mapping',
          fieldKey: 'artifact.entityAliases',
        });
      }
      if (!termSet || Object.keys(termSet).length === 0) {
        issues.push({
          code: 'missing_term_set',
          severity: 'review',
          category: 'data',
          label: isZh ? '语义模型还没有术语集 termSet' : 'Semantic model does not yet define a termSet',
          fieldKey: 'artifact.termSet',
        });
      }
      break;
    }
    case 'workspace-template': {
      const boards = asRecordArray(artifact.boards);
      const dataBindings = asRecordArray(artifact.dataBindings);
      const mobileActions = asRecordArray(artifact.mobileActions);
      const mainPanel = asRecord(artifact.mainPanel);
      if (!mainPanel) {
        issues.push({
          code: 'missing_workspace_main_panel',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'workspace 缺少 mainPanel' : 'Workspace is missing mainPanel',
          fieldKey: 'artifact.mainPanel',
        });
      }
      if (boards.length === 0) {
        issues.push({
          code: 'missing_workspace_boards',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'workspace 缺少 boards' : 'Workspace is missing boards',
          fieldKey: 'artifact.boards',
        });
      }
      if (dataBindings.length === 0) {
        issues.push({
          code: 'missing_workspace_bindings',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'workspace 缺少 dataBindings' : 'Workspace is missing dataBindings',
          fieldKey: 'artifact.dataBindings',
        });
      }
      if (mobileActions.length === 0) {
        issues.push({
          code: 'missing_workspace_mobile_actions',
          severity: 'review',
          category: 'runtime',
          label: isZh ? '建议补充 mobile actions' : 'Recommended: add mobile actions',
          fieldKey: 'artifact.mobileActions',
        });
      }
      break;
    }
    case 'report-model': {
      const dimensions = asRecordArray(artifact.dimensions);
      const measures = asRecordArray(artifact.measures);
      const drillPath = asRecordArray(artifact.drillPath);
      if (dimensions.length === 0) {
        issues.push({
          code: 'missing_report_dimensions',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'report model 缺少 dimensions' : 'Report model is missing dimensions',
          fieldKey: 'artifact.dimensions',
        });
      }
      if (measures.length === 0) {
        issues.push({
          code: 'missing_report_measures',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'report model 缺少 measures' : 'Report model is missing measures',
          fieldKey: 'artifact.measures',
        });
      }
      if (drillPath.length === 0) {
        issues.push({
          code: 'missing_report_drill_path',
          severity: 'review',
          category: 'runtime',
          label: isZh ? '建议补充 drill path' : 'Recommended: add a drill path',
          fieldKey: 'artifact.drillPath',
        });
      }
      break;
    }
    case 'billing-model': {
      const chargeFamilies = asRecordArray(artifact.chargeFamilies);
      const ratingRules = asRecordArray(artifact.ratingRules);
      const postingMap = asRecordArray(artifact.postingMap);
      const invoiceGroupingPolicy = asRecord(artifact.invoiceGroupingPolicy);
      if (chargeFamilies.length === 0) {
        issues.push({
          code: 'missing_billing_charge_families',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'billing model 缺少 charge families' : 'Billing model is missing charge families',
          fieldKey: 'artifact.chargeFamilies',
        });
      }
      if (ratingRules.length === 0) {
        issues.push({
          code: 'missing_billing_rating_rules',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'billing model 缺少 rating rules' : 'Billing model is missing rating rules',
          fieldKey: 'artifact.ratingRules',
        });
      }
      if (postingMap.length === 0) {
        issues.push({
          code: 'missing_billing_posting_map',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'billing model 缺少 posting map' : 'Billing model is missing posting map',
          fieldKey: 'artifact.postingMap',
        });
      }
      if (!invoiceGroupingPolicy) {
        issues.push({
          code: 'missing_billing_grouping_policy',
          severity: 'review',
          category: 'runtime',
          label: isZh ? '建议明确 invoice grouping policy' : 'Recommended: define invoice grouping policy',
          fieldKey: 'artifact.invoiceGroupingPolicy',
        });
      }
      break;
    }
    case 'charge-event': {
      const attributes = asRecordArray(artifact.attributes);
      const captureRules = asRecordArray(artifact.captureRules);
      if (attributes.length === 0) {
        issues.push({
          code: 'missing_charge_event_attributes',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'charge event 缺少 attributes' : 'Charge event is missing attributes',
          fieldKey: 'artifact.attributes',
        });
      }
      if (captureRules.length === 0) {
        issues.push({
          code: 'missing_charge_event_capture_rules',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'charge event 缺少 capture rules' : 'Charge event is missing capture rules',
          fieldKey: 'artifact.captureRules',
        });
      }
      if (!captureRules.some((rule) => hasNonEmptyString(rule.suggestedChargeFamilyCode))) {
        issues.push({
          code: 'missing_charge_event_family_hint',
          severity: 'review',
          category: 'policy',
          label: isZh ? '建议至少一个 capture rule 关联 suggested charge family code' : 'Recommended: at least one capture rule should hint a suggested charge family code',
          fieldKey: 'artifact.captureRules',
        });
      }
      break;
    }
    case 'report-fact': {
      const dimensions = asRecordArray(artifact.dimensions);
      const measures = asRecordArray(artifact.measures);
      const mappingRules = asRecordArray(artifact.mappingRules);
      if (dimensions.length === 0) {
        issues.push({
          code: 'missing_report_fact_dimensions',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'report fact 缺少 dimensions' : 'Report fact is missing dimensions',
          fieldKey: 'artifact.dimensions',
        });
      }
      if (measures.length === 0) {
        issues.push({
          code: 'missing_report_fact_measures',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'report fact 缺少 measures' : 'Report fact is missing measures',
          fieldKey: 'artifact.measures',
        });
      }
      if (mappingRules.length === 0) {
        issues.push({
          code: 'missing_report_fact_mapping_rules',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'report fact 缺少 mapping rules' : 'Report fact is missing mapping rules',
          fieldKey: 'artifact.mappingRules',
        });
      }
      break;
    }
    case 'printable-view-model': {
      const sections = asRecordArray(artifact.sections);
      const actions = asRecordArray(artifact.actions);
      if (!hasNonEmptyString(artifact.documentType)) {
        issues.push({
          code: 'missing_printable_document_type',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'printable view model 缺少 document type' : 'Printable view model is missing a document type',
          fieldKey: 'artifact.documentType',
        });
      }
      if (sections.length === 0) {
        issues.push({
          code: 'missing_printable_sections',
          severity: 'blocking',
          category: 'runtime',
          label: isZh ? 'printable view model 缺少 sections' : 'Printable view model is missing sections',
          fieldKey: 'artifact.sections',
        });
      }
      if (actions.length === 0) {
        issues.push({
          code: 'missing_printable_actions',
          severity: 'review',
          category: 'runtime',
          label: isZh ? '建议补充 printable actions' : 'Recommended: add printable actions',
          fieldKey: 'artifact.actions',
        });
      }
      break;
    }
    case 'bundle': {
      const templates = asRecordArray(artifact.templates);
      const endpoints = asRecordArray(artifact.endpoints);
      const rules = asRecordArray(artifact.rules);
      const menuEntry = asRecord(artifact.menuEntry);

      if (templates.length === 0) {
        issues.push({
          code: 'missing_bundle_templates',
          severity: 'blocking',
          category: 'data',
          label: isZh ? 'Mini app 没有任何界面模板' : 'Mini app has no screen templates',
          fieldKey: 'artifact.templates',
        });
      }
      if (templates.every((template) => !hasNonEmptyString(template.entitySlug))) {
        issues.push({
          code: 'missing_bundle_entities',
          severity: 'blocking',
          category: 'data',
          label: isZh ? 'Mini app 没有明确业务实体' : 'Mini app has no clear business entities',
          fieldKey: 'artifact.templates.entitySlug',
        });
      }
      if (templates.some((template) => !hasNonEmptyString(template.title))) {
        issues.push({
          code: 'missing_bundle_surface_titles',
          severity: 'review',
          category: 'runtime',
          label: isZh ? '部分界面模板缺少标题' : 'Some screen templates are missing titles',
          fieldKey: 'artifact.templates.title',
        });
      }
      if (!menuEntry || !hasNonEmptyString(menuEntry.label)) {
        issues.push({
          code: 'missing_bundle_menu_entry',
          severity: 'review',
          category: 'runtime',
          label: isZh ? 'Mini app 菜单入口未明确' : 'Mini app menu entry is not explicit',
          fieldKey: 'artifact.menuEntry.label',
        });
      }
      if (endpoints.length === 0 && rules.length === 0) {
        issues.push({
          code: 'missing_bundle_runtime_actions',
          severity: 'review',
          category: 'runtime',
          label: isZh ? 'Mini app 尚未定义 API 或自动化规则' : 'Mini app has no API or automation rules yet',
          fieldKey: 'artifact.endpoints|artifact.rules',
        });
      }
      break;
    }
    default:
      break;
  }

  const hasBlocking = issues.some((issue) => issue.severity === 'blocking');
  const hasReview = issues.some((issue) => issue.severity === 'review');

  return {
    decision: hasBlocking ? 'blocked' : hasReview ? 'review_required' : 'ready',
    issues,
    recommendedNextAction: hasBlocking
      ? (isZh ? '先补齐阻塞项，再发布到 package pipeline' : 'Resolve blocking items before publishing into the package pipeline')
      : hasReview
        ? (isZh ? '建议先补充 review 项，再继续 publish / preflight / simulate' : 'Review the highlighted gaps before publish / preflight / simulate')
        : (isZh ? '可以发布，随后继续做 preflight / simulation / install' : 'Ready to publish, then continue with preflight / simulation / install'),
  };
}

// ── Component ──

export default function BuilderStudioPage() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');
  const toast = useToast();
  const mergePageContext = useChatContextStore((state) => state.mergePageContext);

  // State
  const [description, setDescription] = useState('');
  const [artifactType, setArtifactType] = useState<ArtifactType>('view-template');
  const [entityName, setEntityName] = useState('');
  const [viewType, setViewType] = useState<string>('list');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishedId, setPublishedId] = useState<string | null>(null);
  const [publishedSlug, setPublishedSlug] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<PublishResult | null>(null);
  const [exchangeListingId, setExchangeListingId] = useState<string | null>(null);
  const [sharingToExchange, setSharingToExchange] = useState(false);
  const [governanceAuditIds, setGovernanceAuditIds] = useState<Partial<Record<BuilderGovernanceEventType, string>>>({});
  const [governanceError, setGovernanceError] = useState<string | null>(null);
  const [recordingRollback, setRecordingRollback] = useState(false);
  const [publishName, setPublishName] = useState('');
  const [draftDetail, setDraftDetail] = useState<BuilderDraftDetail | null>(null);
  const [loadingDraftDetail, setLoadingDraftDetail] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Generate ──
  const handleGenerate = useCallback(async () => {
    if (!description.trim() || description.length < 10) return;
    setGenerating(true);
    setResult(null);
    setPublishedId(null);
    setPublishedSlug(null);
    setPublishResult(null);
    setExchangeListingId(null);
    setGovernanceAuditIds({});
    setGovernanceError(null);
    setDraftDetail(null);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        description,
        artifactType,
        locale: isZh ? 'zh' : 'en',
      };
      if (artifactType === 'view-template') {
        if (entityName) body.entityName = entityName;
        if (viewType) body.viewType = viewType;
      }

      const endpoint = artifactType === 'bundle' ? '/builder/bundle' : '/builder/generate';
      const data = await api.post<GenerationResult>(endpoint, body);
      setResult(data);
      // Auto-set publish name from artifact
      const art = data.artifact as Record<string, unknown>;
      const generatedName =
        (art.name as string) ||
        (art.title as string) ||
        description.slice(0, 60);
      setPublishName(generatedName);
      recordBuilderGovernanceEvent({
        eventType: 'builder_preview',
        artifactType: data.artifactType,
        artifact: data.artifact,
        name: generatedName,
        description,
      })
        .then((audit) => {
          setGovernanceAuditIds((prev) => ({ ...prev, builder_preview: audit.auditId }));
          setGovernanceError(null);
        })
        .catch((auditErr) => {
          setGovernanceError(auditErr instanceof Error ? auditErr.message : String(auditErr));
        });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setGenerating(false);
    }
  }, [description, artifactType, entityName, viewType, isZh]);

  // ── Publish ──
  const handlePublish = useCallback(async () => {
    if (!result) return;
    setPublishing(true);
    setError(null);
    try {
      const slug = publishName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `app-${Date.now()}`;
      const data = await api.post<PublishResult>('/builder/publish', {
        artifactType: result.artifactType,
        artifact: result.artifact,
        name: publishName || description.slice(0, 60),
        description,
        slug,
      });
      setPublishedId(data.id);
      setPublishedSlug(slug);
      setPublishResult(data);
      setExchangeListingId(null);
      recordBuilderGovernanceEvent({
        eventType: 'builder_publish',
        artifactType: result.artifactType,
        artifact: result.artifact,
        artifactId: data.id,
        name: publishName || description.slice(0, 60),
        description,
        configVersion: `builder-publish:${data.id}`,
        artifactGraphVersion: data.releaseId ? `package-release:${data.releaseId}` : undefined,
      })
        .then((audit) => {
          setGovernanceAuditIds((prev) => ({ ...prev, builder_publish: audit.auditId }));
          setGovernanceError(null);
        })
        .catch((auditErr) => {
          setGovernanceError(auditErr instanceof Error ? auditErr.message : String(auditErr));
        });
      await api.post('/billing/usage-events/consume', {
        actionKey: 'builder_publish',
        units: 1,
        sourceType: 'builder_publish',
        sourceRef: data.id,
        notes: 'Builder publish metering',
        metadata: {
          artifactType: result.artifactType,
          draftId: data.draftId ?? null,
          releaseId: data.releaseId ?? null,
          installId: data.installId ?? null,
        },
      }).catch(() => undefined);
      if (data.draftId) {
        setLoadingDraftDetail(true);
        api.get<BuilderDraftDetail>(`/composer/drafts/${data.draftId}`)
          .then((detail) => setDraftDetail(detail))
          .catch(() => setDraftDetail(null))
          .finally(() => setLoadingDraftDetail(false));
      } else {
        setDraftDetail(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPublishing(false);
    }
  }, [result, publishName, description]);

  const handleRecordRollbackCheckpoint = useCallback(async () => {
    if (!result || !publishedId) return;
    setRecordingRollback(true);
    setGovernanceError(null);
    try {
      const audit = await recordBuilderGovernanceEvent({
        eventType: 'builder_rollback',
        artifactType: result.artifactType,
        artifact: result.artifact,
        artifactId: publishedId,
        name: publishName || publishedSlug || publishedId,
        description,
        configVersion: `builder-rollback:${publishedId}`,
        artifactGraphVersion: publishResult?.releaseId ? `package-release:${publishResult.releaseId}` : undefined,
        summary: `Rollback checkpoint recorded for ${result.artifactType} ${publishedSlug ?? publishedId}.`,
      });
      setGovernanceAuditIds((prev) => ({ ...prev, builder_rollback: audit.auditId }));
    } catch (err) {
      setGovernanceError(err instanceof Error ? err.message : String(err));
    } finally {
      setRecordingRollback(false);
    }
  }, [description, publishName, publishResult?.releaseId, publishedId, publishedSlug, result]);

  const handleShareToExchange = useCallback(async () => {
    if (!publishResult?.releaseId) return;
    setSharingToExchange(true);
    setError(null);
    try {
      const listing = await api.post<{ id: string }>('/package-exchange/listings', {
        packageReleaseId: publishResult.releaseId,
        listingScope: 'group',
        discoverability: 'discoverable',
        approvalRequired: false,
      });
      await api.post(`/package-exchange/listings/${listing.id}/publish`, {});
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_exchange_share',
        units: 1,
        sourceType: 'builder_release',
        sourceRef: publishResult.releaseId,
        notes: 'Builder release shared to exchange',
        metadata: { releaseId: publishResult.releaseId, listingId: listing.id, appId: publishResult.id },
      }).catch(() => undefined);
      setExchangeListingId(listing.id);
      toast.success(isZh ? '已发布到 Package Exchange' : 'Published to Package Exchange');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error(message);
    } finally {
      setSharingToExchange(false);
    }
  }, [publishResult?.releaseId, toast, isZh]);

  // ── History ──
  const loadHistory = useCallback(async () => {
    try {
      const data = await api.get<{ items: HistoryItem[] }>('/builder/history');
      setHistory(data.items);
      setShowHistory(true);
    } catch {
      setHistory([]);
      setShowHistory(true);
    }
  }, []);

  // ── Examples ──
  const examples = isZh ? [
    { text: '创建一个海运进口工作的列表视图，显示工作编号、船名、ETA、状态', type: 'view-template' as ArtifactType },
    { text: '创建一个 warehouse workspace template，包含 main panel、右侧 exceptions assist panel、detail dock 和 mobile scan actions', type: 'workspace-template' as ArtifactType },
    { text: '为 New Revo 创建一个 semantic model，把 master job、container、bundle、container lot 映射到 canonical warehouse roles', type: 'semantic-model' as ArtifactType },
    { text: '创建一个 storage billing report model，包含 customer、warehouse、service date 维度，storage amount 和 bundle count measures，以及从客户到日期到明细的 drill path', type: 'report-model' as ArtifactType },
    { text: '创建一个 bundle-heavy billing model，包含 unloading、storage、release 三个 charge family，按 customer + currency + charge family 汇总开票，并定义 posting map', type: 'billing-model' as ArtifactType },
    { text: '创建一个 charge event 模型，捕获 bundle 收货、上架、出库三个 billable event，并把 bundle.received 对应到 receiving charge family', type: 'charge-event' as ArtifactType },
    { text: '创建一个 report fact 模型，把 bundle.received 和 bundle.released 映射为 warehouse throughput facts，包含 customer、warehouse、service date 维度与 units、revenue measures', type: 'report-fact' as ArtifactType },
    { text: '创建一个 printable view model，用于 GRN 和 bundle receiving slip，包含 header、bundle line table、signoff 和 download pdf 动作', type: 'printable-view-model' as ArtifactType },
    { text: '当工作完成时，自动生成发票并通过邮件通知客户', type: 'reaction-rule' as ArtifactType },
    { text: '创建一个查询客户逾期应收账款的 API 端点', type: 'virtual-endpoint' as ArtifactType },
    { text: '构建一个交付跟踪迷你应用，包含活跃交付列表、详情视图和延迟警报规则', type: 'bundle' as ArtifactType },
  ] : [
    { text: 'Create a list view for sea import jobs showing job number, vessel name, ETA, status', type: 'view-template' as ArtifactType },
    { text: 'Create a warehouse workspace template with a main operations panel, right-side exceptions assist panel, detail dock, and mobile scan actions', type: 'workspace-template' as ArtifactType },
    { text: 'Create a semantic model for New Revo mapping master job, container, bundle, and container lot into canonical warehouse roles', type: 'semantic-model' as ArtifactType },
    { text: 'Create a storage billing report model with customer, warehouse, and service date dimensions, storage amount and bundle count measures, and a drill path from customer to day to detail', type: 'report-model' as ArtifactType },
    { text: 'Create a bundle-heavy billing model with unloading, storage, and release charge families, customer plus currency invoice grouping, and a posting map', type: 'billing-model' as ArtifactType },
    { text: 'Create a charge event model for bundle receiving, putaway, and release, with billable event capture rules and a receiving charge family hint', type: 'charge-event' as ArtifactType },
    { text: 'Create a report fact model that maps bundle.received and bundle.released into warehouse throughput facts with customer, warehouse, and service date dimensions plus units and revenue measures', type: 'report-fact' as ArtifactType },
    { text: 'Create a printable view model for GRN and bundle receiving slips with a header, bundle line table, signoff section, and download PDF action', type: 'printable-view-model' as ArtifactType },
    { text: 'When a job is completed, automatically generate an invoice and notify the customer', type: 'reaction-rule' as ArtifactType },
    { text: 'Create an API endpoint to query overdue customer receivables', type: 'virtual-endpoint' as ArtifactType },
    { text: 'Build a delivery tracking mini-app with active deliveries list, detail view, and delay alerts', type: 'bundle' as ArtifactType },
  ];

  const demoShowcases = useMemo(() => BUSINESS_PACKAGE_SHOWCASES, []);
  const localReadiness = useMemo(
    () => buildLocalReadiness(result, publishName, isZh),
    [result, publishName, isZh],
  );
  const localBlockingIssues = useMemo(
    () => (localReadiness?.issues ?? []).filter((issue) => issue.severity === 'blocking'),
    [localReadiness],
  );
  const localReviewIssues = useMemo(
    () => (localReadiness?.issues ?? []).filter((issue) => issue.severity === 'review'),
    [localReadiness],
  );

  useEffect(() => {
    const missingItems: string[] = [];
    if (!description.trim()) missingItems.push(isZh ? '请先描述要构建的业务能力' : 'Add the business capability description');
    if (!result) missingItems.push(isZh ? '先生成一个有效 artifact' : 'Generate a valid artifact first');
    if (result && !publishName.trim()) missingItems.push(isZh ? '补充发布名称' : 'Add a publish name');
    if (localBlockingIssues.length > 0) missingItems.push(...localBlockingIssues.map((issue) => issue.label));

    const recommendedNextActions = [
      result ? (isZh ? '检查 readiness 和 review 项' : 'Review readiness and review items') : (isZh ? '先生成 artifact' : 'Generate the artifact'),
      publishResult?.draftId ? (isZh ? '进入 draft 做 preflight / simulation' : 'Open the draft for preflight / simulation') : (isZh ? '发布到 package pipeline' : 'Publish into the package pipeline'),
      publishResult?.releaseId ? (isZh ? '按需要分享到 exchange' : 'Share to exchange if needed') : (isZh ? '发布后再决定是否 share' : 'Decide on exchange sharing after publish'),
    ];

    mergePageContext({
      taskContext: {
        taskType: 'builder_compose',
        taskGoal: publishResult?.releaseId
          ? (isZh ? '继续把 builder 产物推进到 install / exchange' : 'Continue the builder artifact into install / exchange')
          : (isZh ? '把自然语言需求转成可发布业务产物' : 'Turn the natural-language brief into a publishable business artifact'),
        currentStep: publishResult?.releaseId
          ? 'published'
          : result
            ? 'review_generated_artifact'
            : 'draft_description',
        completionCriteria: [
          isZh ? '生成有效 artifact' : 'Generate a valid artifact',
          isZh ? '通过本地 readiness 检查' : 'Pass local readiness checks',
          isZh ? '发布 draft/release 并进入 preflight 或 simulation' : 'Publish a draft/release and continue with preflight or simulation',
        ],
        missingItems,
        blockingRisks: localBlockingIssues.map((issue) => issue.label),
        recommendedNextActions,
      },
      executionContext: {
        workspace: 'builder_studio',
        artifactType,
        readinessDecision: localReadiness?.decision ?? 'unknown',
        generated: Boolean(result),
        published: Boolean(publishResult),
        publishStatus: publishResult?.status ?? null,
        exchangeShared: Boolean(exchangeListingId),
      },
      permissionContext: {
        canGenerate: true,
        canPublish: Boolean(result) && localBlockingIssues.length === 0,
        canShareToExchange: Boolean(publishResult?.releaseId),
      },
      contextBudget: {
        detailLevel: 'medium',
        maxKeyFacts: 8,
        preferActionableSummary: true,
      },
    });

    return () => {
      mergePageContext({
        taskContext: undefined,
        executionContext: undefined,
        permissionContext: undefined,
        contextBudget: undefined,
      });
    };
  }, [
    artifactType,
    description,
    exchangeListingId,
    isZh,
    localBlockingIssues,
    localReadiness?.decision,
    mergePageContext,
    publishName,
    publishResult,
    result,
  ]);

  useEffect(() => {
    const demo = getBusinessPackageShowcase(searchParams.get('demo'));
    if (!demo) return;
    setArtifactType(demo.builderArtifactType);
    setDescription(isZh ? demo.builderPromptZh : demo.builderPrompt);
    setPublishName(isZh ? demo.publishNameZh : demo.publishName);
    setShowHistory(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('demo');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams, isZh]);

  useEffect(() => {
    if (searchParams.get('demo')) return;

    const artifactTypeParam = searchParams.get('artifactType');
    const descriptionParam = searchParams.get('description');
    const publishNameParam = searchParams.get('publishName');

    if (!isArtifactType(artifactTypeParam) && !descriptionParam && !publishNameParam) {
      return;
    }

    if (isArtifactType(artifactTypeParam)) {
      setArtifactType(artifactTypeParam);
    }
    if (descriptionParam) {
      setDescription(descriptionParam);
    }
    if (publishNameParam) {
      setPublishName(publishNameParam);
    }
    setShowHistory(false);

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('artifactType');
      next.delete('description');
      next.delete('publishName');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="flex h-full">
      {/* ── Left: Input Panel ── */}
      <div className="w-[420px] border-r flex flex-col bg-background">
        {/* Header */}
        <div className="px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">
              {isZh ? 'Builder Studio' : 'Builder Studio'}
            </h1>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {isZh ? '用自然语言描述你要构建的内容' : 'Describe what you want to build in natural language'}
          </p>
        </div>

        {/* Artifact Type Selection */}
        <div className="px-4 py-3 space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {isZh ? '构建类型' : 'Build Type'}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {ARTIFACT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setArtifactType(opt.value)}
                className={cn(
                  'flex items-start gap-2 p-2.5 rounded-md border text-left transition-all text-sm',
                  artifactType === opt.value
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                    : 'border-border hover:border-muted-foreground/30 hover:bg-muted/30',
                )}
              >
                <span className={cn('mt-0.5', artifactType === opt.value ? 'text-primary' : 'text-muted-foreground')}>
                  {opt.icon}
                </span>
                <div>
                  <div className="font-medium text-xs">{isZh ? opt.labelZh : opt.label}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                    {isZh ? opt.descZh : opt.desc}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* View-template specific fields */}
        {artifactType === 'view-template' && (
          <div className="px-4 pb-2 flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">{isZh ? '实体名' : 'Entity'}</label>
              <input
                className="mt-1 w-full rounded-md border bg-background px-2.5 py-1.5 text-sm"
                placeholder="e.g. jobs, customers"
                value={entityName}
                onChange={e => setEntityName(e.target.value)}
              />
            </div>
            <div className="w-28">
              <label className="text-xs text-muted-foreground">{isZh ? '视图类型' : 'View Type'}</label>
              <select
                className="mt-1 w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                value={viewType}
                onChange={e => setViewType(e.target.value)}
              >
                <option value="list">List</option>
                <option value="detail">Detail</option>
                <option value="form">Form</option>
                <option value="card">Card</option>
              </select>
            </div>
          </div>
        )}

        <Separator />

        {/* Description Input */}
        <div className="flex-1 px-4 py-3 flex flex-col">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            {isZh ? '描述你要构建的内容' : 'Describe what to build'}
          </label>
          <textarea
            className="flex-1 w-full rounded-md border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder={isZh
              ? '例如：创建一个海运进口工作列表，显示工作编号、船名、ETA、起运港、目的港、状态...'
              : 'e.g. Create a list view for sea import jobs showing job number, vessel, ETA, origin, destination, status...'}
            value={description}
            onChange={e => setDescription(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) handleGenerate(); }}
          />

          {/* Quick Examples */}
          <div className="mt-3 space-y-1.5">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
              {isZh ? '快速示例' : 'Quick Examples'}
            </span>
            {examples.map((ex, i) => (
              <button
                key={i}
                className="block w-full text-left text-xs text-muted-foreground hover:text-foreground p-1.5 rounded hover:bg-muted/50 transition-colors"
                onClick={() => { setDescription(ex.text); setArtifactType(ex.type); }}
              >
                <ChevronRight className="inline h-3 w-3 mr-1 opacity-50" />
                {ex.text.slice(0, 80)}{ex.text.length > 80 ? '...' : ''}
              </button>
            ))}
          </div>

          <div className="mt-4 space-y-2">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
              {isZh ? 'Demo Showcase' : 'Demo Showcase'}
            </span>
            <div className="space-y-2">
              {demoShowcases.map((demo) => (
                <div key={demo.id} className="rounded-md border bg-muted/20 p-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-medium">
                        {isZh ? demo.labelZh : demo.label}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <Badge variant={demo.patternLayer === 'foundation' ? 'default' : 'outline'} className="text-[10px]">
                          {demo.patternLayer === 'foundation'
                            ? (isZh ? 'Foundation' : 'Foundation')
                            : (isZh ? 'Specific' : 'Specific')}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px]">
                          {demo.builderArtifactType}
                        </Badge>
                      </div>
                      <div className="mt-1 text-[11px] leading-5 text-muted-foreground">
                        {isZh ? demo.summaryZh : demo.summary}
                      </div>
                      {demo.buildsOn && (
                        <div className="mt-1 text-[11px] leading-5 text-muted-foreground">
                          {isZh ? '建立在' : 'Builds on'}{' '}
                          <span className="font-medium text-foreground">{demo.buildsOn}</span>
                        </div>
                      )}
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {demo.archetypePresetId}
                    </Badge>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      onClick={() => {
                        setArtifactType(demo.builderArtifactType);
                        setDescription(isZh ? demo.builderPromptZh : demo.builderPrompt);
                        setPublishName(isZh ? demo.publishNameZh : demo.publishName);
                      }}
                    >
                      {isZh ? '载入 Demo Prompt' : 'Use Demo Prompt'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[11px]"
                      onClick={() => navigate(`/admin/packs?showcase=${demo.id}`)}
                    >
                      {isZh ? '看 Showcase' : 'Open Showcase'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div className="px-4 py-3 border-t">
          <Button
            className="w-full"
            disabled={generating || description.length < 10}
            onClick={handleGenerate}
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />{isZh ? '生成中...' : 'Generating...'}</>
            ) : (
              <><Send className="h-4 w-4 mr-2" />{isZh ? '生成' : 'Generate'} <kbd className="ml-2 text-[10px] opacity-60">⌘↩</kbd></>
            )}
          </Button>
          <button
            className="mt-2 w-full text-xs text-center text-muted-foreground hover:text-foreground transition-colors"
            onClick={loadHistory}
          >
            <History className="inline h-3 w-3 mr-1" />
            {isZh ? '查看历史生成' : 'View generation history'}
          </button>
        </div>
      </div>

      {/* ── Right: Result / Preview Panel ── */}
      <div className="flex-1 flex flex-col bg-muted/20">
        {!result && !generating && !showHistory && (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Wand2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                {isZh ? '在左侧输入描述，AI 将为你生成构建产物' : 'Enter a description on the left, AI will generate your artifact'}
              </p>
            </div>
          </div>
        )}

        {generating && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary" />
              <p className="text-sm text-muted-foreground">
                {isZh ? 'AI 正在生成...' : 'AI is generating...'}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="m-4 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
            <XCircle className="inline h-4 w-4 mr-1" /> {error}
          </div>
        )}

        {/* History Panel */}
        {showHistory && !result && (
          <div className="flex-1 flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h2 className="font-medium text-sm">{isZh ? '生成历史' : 'Generation History'}</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                {isZh ? '关闭' : 'Close'}
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-2">
                {history.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {isZh ? '暂无历史记录' : 'No history yet'}
                  </p>
                )}
                {history.map(item => (
                  <div key={item.id} className="p-3 rounded-md border bg-background text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant={item.published ? 'default' : 'secondary'} className="text-[10px]">
                        {item.artifact_type}
                      </Badge>
                      {item.published && <Badge variant="outline" className="text-[10px] text-green-600">published</Badge>}
                      {!item.valid && <Badge variant="destructive" className="text-[10px]">invalid</Badge>}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{item.description}</p>
                    {(item.published_slug || item.package_install_id) && (
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                        {item.published_slug && (
                          <Badge variant="outline" className="font-mono">
                            slug: {item.published_slug}
                          </Badge>
                        )}
                        {item.package_install_id && (
                          <Badge variant="outline" className="font-mono">
                            install: {item.package_install_id.slice(0, 8)}
                          </Badge>
                        )}
                        {item.package_release_id && (
                          <Badge variant="outline" className="font-mono">
                            release: {item.package_release_id.slice(0, 8)}
                          </Badge>
                        )}
                        {item.package_id && item.package_version && (
                          <Badge variant="secondary" className="font-mono">
                            {item.package_id}@{item.package_version}
                          </Badge>
                        )}
                        {item.package_runtime_status && (
                          <Badge variant="secondary">
                            {item.package_runtime_status}
                          </Badge>
                        )}
                      </div>
                    )}
                    <p className="mt-1 text-[10px] text-muted-foreground opacity-60">
                      {new Date(item.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Result Panel */}
        {result && (
          <div className="flex-1 flex flex-col">
            {/* Result Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                {result.valid ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-amber-500" />
                )}
                <Badge>{result.artifactType}</Badge>
                <span className="text-sm text-muted-foreground">
                  {result.valid
                    ? (isZh ? '验证通过' : 'Valid')
                    : (isZh ? `${result.errors.length} 个问题` : `${result.errors.length} issue(s)`)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(result.artifact, null, 2));
                  }}
                >
                  <Copy className="h-3.5 w-3.5 mr-1" /> {isZh ? '复制 JSON' : 'Copy JSON'}
                </Button>
              </div>
            </div>

            {/* Errors/Warnings */}
            {result.errors.length > 0 && (
              <div className="px-4 py-2 bg-amber-50 dark:bg-amber-950/20 text-xs space-y-0.5">
                {result.errors.map((e, i) => (
                  <div key={i} className="text-amber-700 dark:text-amber-400">⚠ {e}</div>
                ))}
              </div>
            )}
            {result.warnings && result.warnings.length > 0 && (
              <div className="px-4 py-2 bg-blue-50 dark:bg-blue-950/20 text-xs space-y-0.5">
                {result.warnings.map((w, i) => (
                  <div key={i} className="text-blue-600 dark:text-blue-400">ℹ {w}</div>
                ))}
              </div>
            )}
            {(governanceAuditIds.builder_preview || governanceAuditIds.builder_publish || governanceAuditIds.builder_rollback || governanceError) && (
              <div className="px-4 py-2 border-t bg-muted/20 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-muted-foreground">{isZh ? 'Builder 治理审计' : 'Builder Governance Audit'}</span>
                  {governanceAuditIds.builder_preview && (
                    <Badge variant="outline" className="font-mono">preview: {governanceAuditIds.builder_preview.slice(0, 8)}</Badge>
                  )}
                  {governanceAuditIds.builder_publish && (
                    <Badge variant="outline" className="font-mono">publish: {governanceAuditIds.builder_publish.slice(0, 8)}</Badge>
                  )}
                  {governanceAuditIds.builder_rollback && (
                    <Badge variant="outline" className="font-mono">rollback: {governanceAuditIds.builder_rollback.slice(0, 8)}</Badge>
                  )}
                  {governanceError && (
                    <span className="text-amber-600">{governanceError}</span>
                  )}
                </div>
              </div>
            )}

            {/* Artifact Preview */}
            <ScrollArea className="flex-1">
              <div className="p-4">
                {/* Live DSL Preview for view-templates */}
                {result.artifactType === 'view-template' && (
                  <LiveViewTemplatePreview artifact={result.artifact} />
                )}
                {result.artifactType === 'reaction-rule' && (
                  <ArtifactSummaryReactionRule artifact={result.artifact} />
                )}
                {result.artifactType === 'semantic-model' && (
                  <ArtifactSummarySemanticModel artifact={result.artifact} />
                )}
                {result.artifactType === 'workspace-template' && (
                  <ArtifactSummaryWorkspaceTemplate artifact={result.artifact} />
                )}
                {result.artifactType === 'report-model' && (
                  <ArtifactSummaryReportModel artifact={result.artifact} />
                )}
                {result.artifactType === 'billing-model' && (
                  <ArtifactSummaryBillingModel artifact={result.artifact} />
                )}
                {result.artifactType === 'charge-event' && (
                  <ArtifactSummaryChargeEvent artifact={result.artifact} />
                )}
                {result.artifactType === 'report-fact' && (
                  <ArtifactSummaryReportFact artifact={result.artifact} />
                )}
                {result.artifactType === 'printable-view-model' && (
                  <ArtifactSummaryPrintableViewModel artifact={result.artifact} />
                )}
                {result.artifactType === 'bundle' && (
                  <ArtifactSummaryBundle artifact={result.artifact} />
                )}
                {result.artifactType === 'virtual-endpoint' && (
                  <ArtifactSummaryEndpoint artifact={result.artifact} />
                )}

                <Separator className="my-4" />

                {/* Raw JSON */}
                <details>
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                    {isZh ? '原始 JSON' : 'Raw JSON'}
                  </summary>
                  <pre className="mt-2 p-3 rounded-md bg-muted text-xs font-mono overflow-auto max-h-[400px]">
                    {JSON.stringify(result.artifact, null, 2)}
                  </pre>
                </details>
              </div>
            </ScrollArea>

            {/* Publish Bar */}
            <div className="px-4 py-3 border-t bg-background">
              {publishedId ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-600 text-sm">
                    <CheckCircle2 className="h-4 w-4" />
                    {isZh ? '已发布！' : 'Published!'}
                    <code className="text-xs bg-muted px-1 rounded">{publishedSlug ?? publishedId}</code>
                    {(result?.artifactType === 'view-template' || result?.artifactType === 'bundle') && publishedSlug && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto h-7 text-xs"
                        onClick={() => navigate(`/app/${publishedSlug}`)}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        {isZh ? '打开应用' : 'Open App'}
                      </Button>
                    )}
                  </div>
                  {publishResult && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      {publishResult.draftId && (
                        <Badge variant="outline" className="font-mono">
                          draft: {publishResult.draftId.slice(0, 8)}
                        </Badge>
                      )}
                      {publishResult.releaseId && (
                        <Badge variant="outline" className="font-mono">
                          release: {publishResult.releaseId.slice(0, 8)}
                        </Badge>
                      )}
                      {publishResult.installId && (
                        <Badge variant="outline" className="font-mono">
                          install: {publishResult.installId.slice(0, 8)}
                        </Badge>
                      )}
                      {publishResult.runtimeBindings && publishResult.runtimeBindings.length > 0 && (
                        <Badge variant="secondary">
                          {publishResult.runtimeBindings.length} {isZh ? '个 runtime bindings' : 'runtime bindings'}
                        </Badge>
                      )}
                      {exchangeListingId && (
                        <Badge variant="secondary" className="font-mono">
                          exchange: {exchangeListingId.slice(0, 8)}
                        </Badge>
                      )}
                      </div>
                      {publishResult.releaseId && !exchangeListingId && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={handleShareToExchange}
                          disabled={sharingToExchange}
                        >
                          {sharingToExchange ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <Package className="h-3 w-3 mr-1" />
                          )}
                          {isZh ? '发布到 Exchange' : 'Share to Exchange'}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleRecordRollbackCheckpoint}
                        disabled={recordingRollback}
                      >
                        {recordingRollback ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <GitBranch className="h-3 w-3 mr-1" />
                        )}
                        {isZh ? '记录回滚点' : 'Record Rollback'}
                      </Button>

                      {(draftDetail || loadingDraftDetail) && (
                        <div className="rounded-md border bg-muted/20 p-3 text-xs">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{isZh ? 'Package Readiness' : 'Package Readiness'}</span>
                            {loadingDraftDetail && <Loader2 className="h-3 w-3 animate-spin" />}
                            {draftDetail?.latestPreflight?.decision && (
                              <Badge variant="outline">preflight: {draftDetail.latestPreflight.decision}</Badge>
                            )}
                            {draftDetail?.latestSimulation?.mode && (
                              <Badge variant="outline">simulation: {draftDetail.latestSimulation.mode}</Badge>
                            )}
                          </div>
                          {draftDetail?.latestPreflight?.result?.issues && draftDetail.latestPreflight.result.issues.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {draftDetail.latestPreflight.result.issues.slice(0, 4).map((issue, index) => (
                                <div key={`${issue.label ?? 'issue'}-${index}`} className="rounded border px-2 py-1">
                                  [{issue.severity ?? 'review'} / {issue.category ?? 'runtime'}] {issue.label ?? 'issue'}
                                </div>
                              ))}
                            </div>
                          )}
                          {draftDetail?.latestSimulation?.result?.signals && draftDetail.latestSimulation.result.signals.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {draftDetail.latestSimulation.result.signals.slice(0, 3).map((signal, index) => (
                                <div key={`${signal.label ?? 'signal'}-${index}`} className="rounded border px-2 py-1 text-muted-foreground">
                                  [{signal.severity ?? 'review'} / {signal.category ?? 'runtime'}] {signal.label ?? 'signal'}
                                </div>
                              ))}
                            </div>
                          )}
                          {((draftDetail?.latestSimulation?.result?.mockScenarioRefs?.length ?? 0) > 0
                            || (draftDetail?.latestSimulation?.result?.approvalCheckpoints?.length ?? 0) > 0
                            || (draftDetail?.latestSimulation?.result?.operatorNotes?.length ?? 0) > 0) && (
                            <div className="mt-2 rounded border bg-background/70 p-2 text-muted-foreground">
                              {draftDetail?.latestSimulation?.result?.mockScenarioRefs?.slice(0, 2).map((scenario) => (
                                <div key={scenario}>scenario: {scenario}</div>
                              ))}
                              {draftDetail?.latestSimulation?.result?.approvalCheckpoints && draftDetail.latestSimulation.result.approvalCheckpoints.length > 0 && (
                                <div>approval: {draftDetail.latestSimulation.result.approvalCheckpoints.slice(0, 2).join(', ')}</div>
                              )}
                              {draftDetail?.latestSimulation?.result?.riskFindings && draftDetail.latestSimulation.result.riskFindings.length > 0 && (
                                <div>risk: {draftDetail.latestSimulation.result.riskFindings[0]?.label ?? 'risk finding'}</div>
                              )}
                              {draftDetail?.latestSimulation?.result?.operatorNotes && draftDetail.latestSimulation.result.operatorNotes.length > 0 && (
                                <div>note: {draftDetail.latestSimulation.result.operatorNotes[0]}</div>
                              )}
                            </div>
                          )}
                          {draftDetail?.latestPreflight?.result?.recommendedNextAction && (
                            <div className="mt-2 text-muted-foreground">
                              next: {draftDetail.latestPreflight.result.recommendedNextAction}
                            </div>
                          )}
                          <div className="mt-2 flex gap-2">
                            {publishResult?.draftId && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-[11px]"
                                onClick={() => navigate('/admin/packs')}
                              >
                                {isZh ? '去 Package Manager 看详情' : 'Open Package Manager'}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {localReadiness && (
                    <div className="rounded-md border bg-muted/20 p-3 text-xs">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{isZh ? '本地 Package 预检' : 'Local Package Precheck'}</span>
                        <Badge variant={localReadiness.decision === 'ready' ? 'default' : 'outline'}>
                          {localReadiness.decision}
                        </Badge>
                      </div>
                      {localBlockingIssues.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {localBlockingIssues.map((issue) => (
                            <div key={issue.code} className="rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
                              [{issue.category}] {issue.label}
                            </div>
                          ))}
                        </div>
                      )}
                      {localReviewIssues.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {localReviewIssues.map((issue) => (
                            <div key={issue.code} className="rounded border px-2 py-1 text-muted-foreground">
                              [{issue.category}] {issue.label}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 text-muted-foreground">
                        {localReadiness.recommendedNextAction}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      className="flex-1 rounded-md border bg-background px-2.5 py-1.5 text-sm"
                      placeholder={isZh ? '名称（用于发布）' : 'Name (for publishing)'}
                      value={publishName}
                      onChange={e => setPublishName(e.target.value)}
                    />
                    <Button
                      onClick={handlePublish}
                      disabled={publishing || !publishName.trim() || localReadiness?.decision === 'blocked'}
                    >
                      {publishing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Upload className="h-4 w-4 mr-1" />
                      )}
                      {isZh ? '发布' : 'Publish'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Artifact Summary Sub-Components ──

function previewSampleValue(key: string, type?: string, rowIndex = 0): string {
  const k = key.toLowerCase();
  const t = (type ?? '').toLowerCase();
  if (t === 'date' || k.includes('date') || k.includes('_at') || k.includes('eta') || k.includes('etd'))
    return `2026-0${(rowIndex % 9) + 1}-${10 + rowIndex}`;
  if (t === 'currency' || k.includes('amount') || k.includes('price') || k.includes('total') || k.includes('limit'))
    return `SGD ${(1000 + rowIndex * 250).toLocaleString()}.00`;
  if (t === 'number' || k.includes('count') || k.includes('qty') || k.includes('weight'))
    return String(100 + rowIndex * 50);
  if (k.includes('status') || t === 'status')
    return (['Active', 'Pending', 'Completed', 'Draft', 'Overdue'][rowIndex % 5] ?? 'Active');
  if (k.includes('code') || k.includes('number') || k.includes('no') || k.includes('ref'))
    return `${key.replace(/_/g, '').slice(0, 3).toUpperCase()}-${String(rowIndex + 1).padStart(4, '0')}`;
  if (k.includes('name') || k.includes('customer') || k.includes('vendor'))
    return (['Maersk Line', 'CMA CGM', 'COSCO Shipping', 'Evergreen Marine', 'Yang Ming'][rowIndex % 5] ?? 'Maersk Line');
  if (k.includes('port') || k.includes('origin') || k.includes('destination'))
    return (['SGSIN', 'CNSHA', 'HKHKG', 'JPNGO', 'KRPUS'][rowIndex % 5] ?? 'SGSIN');
  if (k.includes('email')) return `user${rowIndex + 1}@example.com`;
  if (k.includes('phone') || k.includes('tel')) return `+65 9${100 + rowIndex} ${1000 + rowIndex * 111}`;
  if (k.includes('terms')) return (['NET 30', 'NET 60', 'COD', 'NET 14', 'NET 45'][rowIndex % 5] ?? 'NET 30');
  if (t === 'boolean' || k.includes('enabled') || k.includes('active')) return rowIndex % 2 === 0 ? 'Yes' : 'No';
  return `${key.replace(/_/g, ' ')} ${rowIndex + 1}`;
}

function LiveViewTemplatePreview({ artifact }: { artifact: Record<string, unknown> }) {
  const [surface, setSurface] = useState<'desktop' | 'mobile'>('desktop');
  const columns = (artifact.columns ?? []) as Array<Record<string, unknown>>;
  const filters = (artifact.filters ?? []) as Array<Record<string, unknown>>;
  const sections = (artifact.sections ?? []) as Array<Record<string, unknown>>;
  const fields = (artifact.fields ?? []) as Array<Record<string, unknown>>;
  const viewType = artifact.viewType as string ?? 'list';
  const title = artifact.title as string ?? artifact.entitySlug as string ?? 'Generated View';

  const rows = useMemo(
    () => Array.from({ length: 5 }, (_, ri) => {
      const row: Record<string, string> = {};
      for (const col of columns) { const ck = (col.field ?? col.key ?? '') as string; row[ck] = previewSampleValue(ck, (col.type ?? col.format ?? '') as string, ri); }
      return row;
    }),
    [columns],
  );

  return (
    <div className="space-y-3">
      {/* Preview header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">{title}</span>
          <Badge variant="outline" className="text-[10px]">{viewType}</Badge>
          {columns.length > 0 && (
            <span className="text-[10px] text-muted-foreground">{columns.length} columns</span>
          )}
        </div>
        <div className="flex gap-1">
          <Button variant={surface === 'desktop' ? 'secondary' : 'ghost'} size="sm" className="h-6 w-6 p-0" onClick={() => setSurface('desktop')}>
            <Monitor className="h-3 w-3" />
          </Button>
          <Button variant={surface === 'mobile' ? 'secondary' : 'ghost'} size="sm" className="h-6 w-6 p-0" onClick={() => setSurface('mobile')}>
            <Smartphone className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* List view preview */}
      {(viewType === 'list' || viewType === 'card') && columns.length > 0 && (
        <div className={cn('rounded-lg border bg-white overflow-hidden', surface === 'mobile' && 'max-w-[380px] mx-auto')}>
          {/* Filter bar */}
          {filters.length > 0 && (
            <div className="flex gap-2 p-3 border-b bg-muted/20 flex-wrap">
              {filters.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 bg-background rounded-md text-xs text-muted-foreground border">
                  <Filter className="h-3 w-3" />
                  {(f.label ?? f.field ?? f.key) as string}
                </div>
              ))}
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-background rounded-md text-xs text-muted-foreground border ml-auto">
                <Search className="h-3 w-3" />
                Search...
              </div>
            </div>
          )}

          {surface === 'mobile' ? (
            /* Mobile card view */
            <div className="p-3 space-y-2">
              {rows.map((row, ri) => (
                <div key={ri} className="p-3 bg-muted/10 rounded-lg border">
                  <div className="font-semibold text-sm">{row[(columns[0]?.field ?? columns[0]?.key ?? '') as string] ?? `Row ${ri + 1}`}</div>
                  {columns.slice(1, 4).map((col, ci) => {
                    const ck = (col.field ?? col.key ?? '') as string;
                    const val = row[ck] ?? '';
                    const isStatus = ck.includes('status') || col.type === 'status' || col.format === 'status';
                    return (
                      <div key={ci} className="flex items-center justify-between mt-1 text-xs">
                        <span className="text-muted-foreground">{col.label as string}</span>
                        {isStatus ? (
                          <Badge variant="secondary" className={cn('text-[10px]', statusBadgeColor(val))}>{val}</Badge>
                        ) : (
                          <span>{val}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (
            /* Desktop table view */
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      {columns.map((col, i) => (
                        <th key={i} className="px-4 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs">
                          <div className="flex items-center gap-1">
                            {col.label as string}
                            {col.sortable !== false && <ArrowUpDown className="h-3 w-3 opacity-40" />}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, ri) => (
                      <tr key={ri} className="border-b hover:bg-muted/20 cursor-pointer transition-colors">
                        {columns.map((col, ci) => {
                          const ck = (col.field ?? col.key ?? '') as string;
                          const val = row[ck] ?? '';
                          const isStatus = ck.includes('status') || col.type === 'status' || col.format === 'status';
                          const isCurrency = col.type === 'currency' || col.format === 'currency' || ck.includes('amount') || ck.includes('limit');
                          return (
                            <td key={ci} className={cn('px-4 py-2.5 whitespace-nowrap text-xs', isCurrency && 'text-right font-mono')}>
                              {isStatus ? (
                                <Badge variant="secondary" className={cn('text-[10px]', statusBadgeColor(val))}>{val}</Badge>
                              ) : ci === 0 ? (
                                <span className="font-medium text-primary">{val}</span>
                              ) : (
                                val
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground border-t">
                <span>Showing 1–5 of 42 records</span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" disabled>Prev</Button>
                  <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 bg-primary/10">1</Button>
                  <Button variant="outline" size="sm" className="h-6 text-[10px] px-2">2</Button>
                  <Button variant="outline" size="sm" className="h-6 text-[10px] px-2">Next</Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Detail view preview */}
      {viewType === 'detail' && sections.length > 0 && (
        <div className="rounded-lg border bg-white p-4 space-y-5">
          {sections.map((sec, si) => (
            <div key={si}>
              {!!sec.title && <h3 className="text-sm font-semibold mb-3 text-foreground">{sec.title as string}</h3>}
              <div className="grid grid-cols-2 gap-3">
                {((sec.fields ?? []) as Array<Record<string, unknown>>).map((field, fi) => (
                  <div key={fi} className={cn((field.colSpan as number) === 2 && 'col-span-2')}>
                    <div className="text-[10px] text-muted-foreground mb-0.5">{field.label as string}</div>
                    <div className="text-sm font-medium">{previewSampleValue((field.field ?? field.key ?? '') as string, (field.type ?? field.format ?? '') as string, fi)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form view preview */}
      {viewType === 'form' && (sections.length > 0 || fields.length > 0) && (
        <div className="rounded-lg border bg-white p-4 space-y-5">
          {sections.length > 0 ? sections.map((sec, si) => (
            <div key={si}>
              {!!sec.title && <h3 className="text-sm font-semibold mb-3">{sec.title as string}</h3>}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {((sec.fields ?? []) as Array<Record<string, unknown>>).map((field, fi) => (
                  <div key={fi} className={cn((field.colSpan as number) === 2 && 'col-span-2')}>
                    <label className="text-[10px] text-muted-foreground mb-1 block">{field.label as string}{!!field.required && <span className="text-destructive ml-0.5">*</span>}</label>
                    <div className="h-8 border rounded-md bg-muted/20 px-2 flex items-center text-xs text-muted-foreground">
                      {previewSampleValue((field.field ?? field.key ?? '') as string, (field.type ?? field.format ?? '') as string, fi)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {fields.map((field, fi) => (
                <div key={fi} className={cn((field.colSpan as number) === 2 && 'col-span-2')}>
                  <label className="text-[10px] text-muted-foreground mb-1 block">{field.label as string}{!!field.required && <span className="text-destructive ml-0.5">*</span>}</label>
                  <div className="h-8 border rounded-md bg-muted/20 px-2 flex items-center text-xs text-muted-foreground">
                    {previewSampleValue((field.field ?? field.key ?? '') as string, (field.type ?? field.format ?? '') as string, fi)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fallback if no renderable content */}
      {columns.length === 0 && sections.length === 0 && fields.length === 0 && (
        <div className="p-4 text-center text-xs text-muted-foreground">
          <LayoutList className="h-6 w-6 mx-auto mb-2 opacity-30" />
          No columns or fields generated. Try a more specific description.
        </div>
      )}
    </div>
  );
}

function statusBadgeColor(val: string): string {
  const v = (val ?? '').toLowerCase();
  if (v === 'active' || v === 'completed') return 'bg-emerald-100 text-emerald-800';
  if (v === 'pending' || v === 'draft') return 'bg-amber-100 text-amber-800';
  if (v === 'overdue' || v === 'cancelled') return 'bg-red-100 text-red-800';
  return 'bg-blue-100 text-blue-800';
}

function ArtifactSummaryReactionRule({ artifact }: { artifact: Record<string, unknown> }) {
  const actions = (artifact.actions ?? []) as Array<Record<string, unknown>>;
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">{artifact.name as string}</div>
      <div className="flex items-center gap-2 text-xs">
        <Badge variant="outline">Event: {artifact.eventType as string}</Badge>
        {!!artifact.condition && <Badge variant="secondary">Condition: {artifact.condition as string}</Badge>}
      </div>
      <div className="space-y-1.5 mt-2">
        {actions.map((action, i) => (
          <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-muted text-xs">
            <Zap className="h-3 w-3 text-amber-500" />
            <span className="font-medium">{action.type as string}</span>
            {!!action.config && (
              <span className="text-muted-foreground">
                {JSON.stringify(action.config).slice(0, 60)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ArtifactSummarySemanticModel({ artifact }: { artifact: Record<string, unknown> }) {
  const entityAliases = asRecordArray(artifact.entityAliases);
  const fieldAliases = asRecordArray(artifact.fieldAliases);
  const statusAliases = asRecordArray(artifact.statusAliases);
  const termSet = asRecord(artifact.termSet);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <GitBranch className="h-4 w-4 text-primary" />
        <span className="font-medium">{(artifact.name as string) ?? 'Semantic Model'}</span>
        {!!artifact.version && <Badge variant="outline" className="text-[10px]">v{artifact.version as string}</Badge>}
        {!!artifact.canonicalVersion && <Badge variant="secondary" className="text-[10px]">canonical {artifact.canonicalVersion as string}</Badge>}
      </div>
      {!!artifact.description && (
        <p className="text-xs text-muted-foreground">{artifact.description as string}</p>
      )}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{entityAliases.length}</div>
          <div className="text-[10px] text-muted-foreground">Entity Aliases</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{fieldAliases.length}</div>
          <div className="text-[10px] text-muted-foreground">Field Aliases</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{statusAliases.length}</div>
          <div className="text-[10px] text-muted-foreground">Status Aliases</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{termSet ? Object.keys(termSet).length : 0}</div>
          <div className="text-[10px] text-muted-foreground">Terms</div>
        </div>
      </div>
      {entityAliases.length > 0 && (
        <div className="space-y-1">
          {entityAliases.slice(0, 8).map((entry, index) => (
            <div key={`${entry.alias ?? 'alias'}-${index}`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted">
              <Badge variant="outline" className="text-[10px]">{entry.canonicalRole as string}</Badge>
              <span className="font-medium">{entry.alias as string}</span>
              {!!entry.displayLabel && <span className="text-muted-foreground">{entry.displayLabel as string}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArtifactSummaryWorkspaceTemplate({ artifact }: { artifact: Record<string, unknown> }) {
  const boards = asRecordArray(artifact.boards);
  const queues = asRecordArray(artifact.queues);
  const dataBindings = asRecordArray(artifact.dataBindings);
  const mobileActions = asRecordArray(artifact.mobileActions);
  const actionPlacements = asRecordArray(artifact.actionPlacements);
  const mainPanel = asRecord(artifact.mainPanel);
  const assistPanel = asRecord(artifact.assistPanel);
  const detailDock = asRecord(artifact.detailDock);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <Monitor className="h-4 w-4 text-primary" />
        <span className="font-medium">{(artifact.name as string) ?? 'Workspace Template'}</span>
        {!!artifact.version && <Badge variant="outline" className="text-[10px]">v{artifact.version as string}</Badge>}
        {!!artifact.canonicalVersion && <Badge variant="secondary" className="text-[10px]">canonical {artifact.canonicalVersion as string}</Badge>}
      </div>
      {!!artifact.description && (
        <p className="text-xs text-muted-foreground">{artifact.description as string}</p>
      )}
      <div className="grid grid-cols-5 gap-3">
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{boards.length}</div>
          <div className="text-[10px] text-muted-foreground">Boards</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{queues.length}</div>
          <div className="text-[10px] text-muted-foreground">Queues</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{dataBindings.length}</div>
          <div className="text-[10px] text-muted-foreground">Bindings</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{mobileActions.length}</div>
          <div className="text-[10px] text-muted-foreground">Mobile Actions</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{actionPlacements.length}</div>
          <div className="text-[10px] text-muted-foreground">Placements</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="p-2 rounded-md bg-muted">
          <div className="font-medium">Main</div>
          <div className="text-muted-foreground mt-1">{mainPanel?.type ? String(mainPanel.type) : 'missing'}</div>
        </div>
        <div className="p-2 rounded-md bg-muted">
          <div className="font-medium">Assist</div>
          <div className="text-muted-foreground mt-1">{assistPanel?.enabled ? String(assistPanel.mode ?? 'enabled') : 'disabled'}</div>
        </div>
        <div className="p-2 rounded-md bg-muted">
          <div className="font-medium">Dock</div>
          <div className="text-muted-foreground mt-1">{detailDock?.enabled ? String(detailDock.mode ?? 'enabled') : 'disabled'}</div>
        </div>
      </div>
      {boards.length > 0 && (
        <div className="space-y-1">
          {boards.slice(0, 6).map((board, index) => {
            const sections = asRecordArray(board.sections);
            return (
              <div key={`${String(board.id ?? 'board')}-${index}`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted">
                <Badge variant="outline" className="text-[10px]">{String(board.layout ?? 'single')}</Badge>
                <span className="font-medium">{String(board.label ?? board.id ?? `Board ${index + 1}`)}</span>
                <span className="text-muted-foreground">{sections.length} sections</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ArtifactSummaryReportModel({ artifact }: { artifact: Record<string, unknown> }) {
  const dimensions = asRecordArray(artifact.dimensions);
  const measures = asRecordArray(artifact.measures);
  const filters = asRecordArray(artifact.filters);
  const drillPath = asRecordArray(artifact.drillPath);
  const visualHints = asRecord(artifact.visualHints);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="font-medium">{(artifact.name as string) ?? 'Report Model'}</span>
        {!!artifact.version && <Badge variant="outline" className="text-[10px]">v{artifact.version as string}</Badge>}
        {!!artifact.reportType && <Badge variant="secondary" className="text-[10px]">{artifact.reportType as string}</Badge>}
      </div>
      {!!artifact.description && <p className="text-xs text-muted-foreground">{artifact.description as string}</p>}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{dimensions.length}</div>
          <div className="text-[10px] text-muted-foreground">Dimensions</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{measures.length}</div>
          <div className="text-[10px] text-muted-foreground">Measures</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{filters.length}</div>
          <div className="text-[10px] text-muted-foreground">Filters</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{drillPath.length}</div>
          <div className="text-[10px] text-muted-foreground">Drill Path</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="p-2 rounded-md bg-muted">
          <div className="font-medium">Chart</div>
          <div className="text-muted-foreground mt-1">{String(visualHints?.defaultChart ?? 'table')}</div>
        </div>
        <div className="p-2 rounded-md bg-muted">
          <div className="font-medium">Granularity</div>
          <div className="text-muted-foreground mt-1">{String(visualHints?.defaultGranularity ?? 'none')}</div>
        </div>
      </div>
      {measures.length > 0 && (
        <div className="space-y-1">
          {measures.slice(0, 6).map((measure, index) => (
            <div key={`${String(measure.id ?? 'measure')}-${index}`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted">
              <Badge variant="outline" className="text-[10px]">{String(measure.aggregation ?? 'sum')}</Badge>
              <span className="font-medium">{String(measure.label ?? measure.id ?? `Measure ${index + 1}`)}</span>
              <span className="text-muted-foreground">{String(measure.field ?? '')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArtifactSummaryBillingModel({ artifact }: { artifact: Record<string, unknown> }) {
  const chargeFamilies = asRecordArray(artifact.chargeFamilies);
  const ratingRules = asRecordArray(artifact.ratingRules);
  const postingMap = asRecordArray(artifact.postingMap);
  const billableEventGate = asRecordArray(artifact.billableEventGate);
  const invoiceGroupingPolicy = asRecord(artifact.invoiceGroupingPolicy);
  const groupBy = Array.isArray(invoiceGroupingPolicy?.groupBy) ? invoiceGroupingPolicy.groupBy : [];
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <Calculator className="h-4 w-4 text-primary" />
        <span className="font-medium">{(artifact.name as string) ?? 'Billing Model'}</span>
        {!!artifact.version && <Badge variant="outline" className="text-[10px]">v{artifact.version as string}</Badge>}
      </div>
      {!!artifact.description && <p className="text-xs text-muted-foreground">{artifact.description as string}</p>}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{chargeFamilies.length}</div>
          <div className="text-[10px] text-muted-foreground">Charge Families</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{ratingRules.length}</div>
          <div className="text-[10px] text-muted-foreground">Rating Rules</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{postingMap.length}</div>
          <div className="text-[10px] text-muted-foreground">Posting Map</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{billableEventGate.length}</div>
          <div className="text-[10px] text-muted-foreground">Event Gates</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="p-2 rounded-md bg-muted">
          <div className="font-medium">Cycle</div>
          <div className="text-muted-foreground mt-1">{String(invoiceGroupingPolicy?.cycle ?? 'manual')}</div>
        </div>
        <div className="p-2 rounded-md bg-muted">
          <div className="font-medium">Group By</div>
          <div className="text-muted-foreground mt-1">{groupBy.map(String).join(', ') || 'none'}</div>
        </div>
      </div>
      {chargeFamilies.length > 0 && (
        <div className="space-y-1">
          {chargeFamilies.slice(0, 6).map((family, index) => (
            <div key={`${String(family.id ?? 'family')}-${index}`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted">
              <Badge variant="outline" className="text-[10px]">{String(family.billableUnit ?? 'custom')}</Badge>
              <span className="font-medium">{String(family.label ?? family.code ?? `Family ${index + 1}`)}</span>
              <span className="text-muted-foreground">{String(family.code ?? '')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArtifactSummaryChargeEvent({ artifact }: { artifact: Record<string, unknown> }) {
  const attributes = asRecordArray(artifact.attributes);
  const captureRules = asRecordArray(artifact.captureRules);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <Zap className="h-4 w-4 text-primary" />
        <span className="font-medium">{(artifact.name as string) ?? 'Charge Event'}</span>
        {!!artifact.version && <Badge variant="outline" className="text-[10px]">v{artifact.version as string}</Badge>}
      </div>
      {!!artifact.description && <p className="text-xs text-muted-foreground">{artifact.description as string}</p>}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{attributes.length}</div>
          <div className="text-[10px] text-muted-foreground">Attributes</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{captureRules.length}</div>
          <div className="text-[10px] text-muted-foreground">Capture Rules</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{captureRules.filter((rule) => hasNonEmptyString(rule.suggestedChargeFamilyCode)).length}</div>
          <div className="text-[10px] text-muted-foreground">Charge Hints</div>
        </div>
      </div>
      {captureRules.length > 0 && (
        <div className="space-y-1">
          {captureRules.slice(0, 6).map((rule, index) => (
            <div key={`${String(rule.id ?? 'capture-rule')}-${index}`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted">
              <Badge variant="outline" className="text-[10px]">{String(rule.sourceRole ?? 'custom')}</Badge>
              <span className="font-medium">{String(rule.label ?? rule.eventType ?? `Rule ${index + 1}`)}</span>
              <span className="text-muted-foreground">{String(rule.eventType ?? '')}</span>
              {!!rule.suggestedChargeFamilyCode && <Badge variant="secondary" className="text-[10px]">{String(rule.suggestedChargeFamilyCode)}</Badge>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArtifactSummaryReportFact({ artifact }: { artifact: Record<string, unknown> }) {
  const dimensions = asRecordArray(artifact.dimensions);
  const measures = asRecordArray(artifact.measures);
  const mappingRules = asRecordArray(artifact.mappingRules);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <History className="h-4 w-4 text-primary" />
        <span className="font-medium">{(artifact.name as string) ?? 'Report Fact'}</span>
        {!!artifact.version && <Badge variant="outline" className="text-[10px]">v{artifact.version as string}</Badge>}
        {!!artifact.canonicalVersion && <Badge variant="secondary" className="text-[10px]">canonical {artifact.canonicalVersion as string}</Badge>}
      </div>
      {!!artifact.description && <p className="text-xs text-muted-foreground">{artifact.description as string}</p>}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{dimensions.length}</div>
          <div className="text-[10px] text-muted-foreground">Dimensions</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{measures.length}</div>
          <div className="text-[10px] text-muted-foreground">Measures</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{mappingRules.length}</div>
          <div className="text-[10px] text-muted-foreground">Mappings</div>
        </div>
      </div>
      {mappingRules.length > 0 && (
        <div className="space-y-1">
          {mappingRules.slice(0, 6).map((rule, index) => {
            const measureBindings = asRecordArray(rule.measureBindings);
            return (
              <div key={`${String(rule.id ?? 'mapping-rule')}-${index}`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted">
                <Badge variant="outline" className="text-[10px]">{String(rule.grain ?? 'event')}</Badge>
                <span className="font-medium">{String(rule.label ?? rule.id ?? `Mapping ${index + 1}`)}</span>
                <span className="text-muted-foreground">{String(rule.sourceRole ?? 'custom')}</span>
                <span className="text-muted-foreground">{measureBindings.length} measures</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ArtifactSummaryPrintableViewModel({ artifact }: { artifact: Record<string, unknown> }) {
  const sections = asRecordArray(artifact.sections);
  const actions = asRecordArray(artifact.actions);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <Copy className="h-4 w-4 text-primary" />
        <span className="font-medium">{(artifact.name as string) ?? 'Printable View Model'}</span>
        {!!artifact.documentType && <Badge variant="outline" className="text-[10px]">{artifact.documentType as string}</Badge>}
      </div>
      {!!artifact.description && <p className="text-xs text-muted-foreground">{artifact.description as string}</p>}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{sections.length}</div>
          <div className="text-[10px] text-muted-foreground">Sections</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{actions.length}</div>
          <div className="text-[10px] text-muted-foreground">Actions</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{sections.filter((section) => section.type === 'table').length}</div>
          <div className="text-[10px] text-muted-foreground">Tables</div>
        </div>
      </div>
      {sections.length > 0 && (
        <div className="space-y-1">
          {sections.slice(0, 6).map((section, index) => {
            const fields = asRecordArray(section.fields);
            return (
              <div key={`${String(section.id ?? 'section')}-${index}`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted">
                <Badge variant="outline" className="text-[10px]">{String(section.type ?? 'summary')}</Badge>
                <span className="font-medium">{String(section.label ?? section.id ?? `Section ${index + 1}`)}</span>
                <span className="text-muted-foreground">{String(section.sourceRole ?? 'custom')}</span>
                <span className="text-muted-foreground">{fields.length} fields</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ArtifactSummaryBundle({ artifact }: { artifact: Record<string, unknown> }) {
  const templates = (artifact.templates ?? []) as unknown[];
  const endpoints = (artifact.endpoints ?? []) as Array<Record<string, unknown>>;
  const rules = (artifact.rules ?? []) as Array<Record<string, unknown>>;
  const menu = artifact.menuEntry as Record<string, unknown> | undefined;
  return (
    <div className="space-y-3">
      {menu && (
        <div className="flex items-center gap-2 text-sm">
          <Package className="h-4 w-4 text-primary" />
          <span className="font-medium">{menu.label as string}</span>
          {!!menu.category && <Badge variant="outline" className="text-[10px]">{menu.category as string}</Badge>}
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{templates.length}</div>
          <div className="text-[10px] text-muted-foreground">Templates</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{endpoints.length}</div>
          <div className="text-[10px] text-muted-foreground">Endpoints</div>
        </div>
        <div className="p-3 rounded-md border text-center">
          <div className="text-2xl font-bold">{rules.length}</div>
          <div className="text-[10px] text-muted-foreground">Rules</div>
        </div>
      </div>
      {endpoints.length > 0 && (
        <div className="space-y-1">
          {endpoints.map((ep, i) => (
            <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted">
              <Badge variant="outline" className="text-[10px] font-mono">{ep.method as string}</Badge>
              <span className="font-mono">{ep.slug as string}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArtifactSummaryEndpoint({ artifact }: { artifact: Record<string, unknown> }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="font-mono">{artifact.method as string}</Badge>
        <span className="text-sm font-mono font-medium">/{artifact.slug as string}</span>
      </div>
      <div className="text-sm">{artifact.name as string}</div>
      {!!artifact.description && (
        <p className="text-xs text-muted-foreground">{artifact.description as string}</p>
      )}
      <div className="flex gap-2">
        <Badge variant="secondary" className="text-[10px]">Type: {artifact.execution_type as string}</Badge>
        {!!artifact.rate_limit && <Badge variant="secondary" className="text-[10px]">Rate: {artifact.rate_limit as number}/min</Badge>}
      </div>
    </div>
  );
}
